import React, { FC } from 'react';
import { Box, CardActionArea, Stack, useTheme, Badge } from '@mui/material';

import { Typography } from '../Typography/Typography';
import { Icon } from '../Icon/Icon';
import { Chip } from '../Chip/Chip';
import { TruncatedBox } from '../TruncatedBox/TruncatedBox';

export interface DishSummaryProps {
  /**
   * The URL of the dish image
   */
  image: string;
  /**
   * The title of the dish
   */
  title: string;
  /**
   * Tags for the dish (e.g. for Meal Kits)
   */
  tags?: string[];
  /**
   * Protein options for meal kits with quantities (e.g., [{name: "Chicken Thighs", quantity: "2x"}])
   */
  selectedVariants?: Array<{ name: string; quantity: string }>;
  /**
   * The quantity of this dish selected
   */
  quantity?: number;
  /**
   * Callback when dish item is clicked
   */
  onViewDetails: () => void;
  /**
   * v2 style
   */
  v2?: boolean;
}

export const DishSummary: FC<DishSummaryProps> = ({
  image,
  title,
  tags = [],
  selectedVariants = [],
  quantity = 0,
  onViewDetails,
  v2 = false,
}) => {
  const theme = useTheme();

  const backgroundColor = v2
    ? theme.palette.primary.background
    : theme.palette.secondary.light;
  const borderColor = v2
    ? theme.palette.grey[500]
    : theme.palette.secondary.dark;

  return (
    <CardActionArea onClick={onViewDetails}>
      <Stack
        direction="row"
        spacing={2}
        alignItems="flex-start"
        sx={{
          width: '100%',
        }}
      >
        {/* Image with Badge */}
        <Box position="relative">
          <Badge
            badgeContent={quantity > 0 ? quantity : null}
            sx={{
              '& .MuiBadge-badge': {
                width: '32px',
                height: '32px',
                left: '4px',
                top: '4px',
                background: backgroundColor,
                border: `1px solid ${borderColor}`,
                borderRadius: '100px',
                fontWeight: 700,
                color: theme.palette.text.primary,
                transform: 'none',
              },
            }}
          >
            <img
              src={image || '/images/empty_image.png'}
              alt={title}
              style={{
                width: '80px',
                height: '80px',
                borderRadius: '8px',
                objectFit: 'cover',
              }}
            />
          </Badge>
        </Box>

        {/* Title and Tags Section */}
        <Box
          sx={{
            flex: 1,
          }}
        >
          <Stack spacing={0.5}>
            <Typography variant="body1" noWrap>
              {title}
            </Typography>

            {tags.length > 0 && (
              <TruncatedBox lines={2}>
                <Typography variant="caption" color="text.secondary">
                  {tags.join(', ')}
                </Typography>
              </TruncatedBox>
            )}

            {/* Protein options as chips */}
            {selectedVariants.length > 0 && (
              <Stack
                direction="row"
                flexWrap="wrap"
                spacing={0.5}
                sx={{ mt: 1 }}
              >
                {selectedVariants.map((protein) => (
                  <Chip
                    key={`${protein.name}-${protein.quantity}`}
                    label={
                      <span>
                        <span style={{ fontWeight: 700 }}>
                          {protein.quantity}
                        </span>{' '}
                        {protein.name}
                      </span>
                    }
                    size="small"
                    variant="outlined"
                    sx={{
                      height: '28px',
                      background: backgroundColor,
                      border: `1px solid ${borderColor}`,
                      '& .MuiChip-label': {
                        fontSize: '12px',
                        fontWeight: 400,
                      },
                    }}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        </Box>

        {/* Icon */}
        <Box alignSelf="center">
          <Icon
            name="chevron-right"
            size="xlarge"
            fill={theme.palette.text.primary}
          />
        </Box>
      </Stack>
    </CardActionArea>
  );
};
